import { Box, Button, Flex, FormControl, HStack, Stack, Text, VStack } from "@chakra-ui/react";
import { useGetBuyQuotesMutation, useGetQuoteConfigQuery } from "apis/swap";
import { CircularLoader, CryptoTxInput, ExchangeInfoAccordion, ExchangeInfoDetail, Input, InputLabel, SwapInfo } from "components";
import configs from "config";
import { add } from "date-fns";
import { useCountry, useForm, usePartialState, useQuoteRate, useRedebounce } from "hooks";
import { IQuote, IQuoteRo, PageProps } from "interfaces";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  MathRound,
  addCommas,
  currencyFormat,
  getCoinPrecision,
  isFiat,
  removeComma,
  toCoinLocale,
  toPrecision,
  when,
} from "utils";
import ls from "utils/secureStorage";
import { toUpperTrim } from "utils/textFormatter";

interface SellPanelProps extends PageProps {
  authToken: string;
}
export function SellPanel({ authToken }: SellPanelProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const redebounce = useRedebounce();
  const { currency } = useCountry();

  const previewQuote = useRef(true);
  const lastPersisted = useRef(false);

  const kycRequired = useRef(ls.get(configs.KYC_REQUIRED));

  const sync = useRef(true);
  const qResRef = useRef<IQuoteRo | null>(null);
  const initTime = useRef(new Date());

  const { quoteRes: qRes, setSellQuote, setQuoteRes, isActiveQuote } = useForm();

  const [isPersistingQuote, setIsPersistingQuote] = useState(false);
  const isPersistingQuoteRef = useRef(false);

  const queries = useMemo(() => new URLSearchParams(location?.search), [location]);

  const tokens = useMemo(() => {
    return Object.keys(configs.coins).filter((tk) => !isFiat(tk) && !["tbtc", "teth"].includes(tk));
  }, []);

  const coinToFiatRate = useRef<number>(0);
  const [quoteRes, setRes] = useState<IQuoteRo | null>(qRes);

  const [state, set] = usePartialState<IQuote>({
    fiatType: when(queries.has("fiat") && isFiat(queries.get("fiat")!), queries.get("fiat")?.toLowerCase()!, currency),
    cryptoType: when(
      queries.has("coin") && tokens.includes(queries.get("coin")?.toLowerCase()!),
      queries.get("coin")!,
      configs.DEFAULT_COIN
    ),
    network: when(queries.has("network"), queries.get("network")!, undefined),
    memo: when(queries.has("memo"), queries.get("memo")!, undefined),
    tagNumber: when(queries.has("tagNumber"), queries.get("tagNumber")!, undefined),
    fiatAmount: when(queries.has("fiatAmount"), queries.get("fiatAmount")!, undefined),
    cryptoAmount: when(queries.has("cryptoAmount"), queries.get("cryptoAmount")!, undefined),
  });

  const { data: configData, isLoading: configLoading } = useGetQuoteConfigQuery({
    cryptoType: state.cryptoType!,
    fiatType: state.fiatType!,
    type: "sell",
  });

  useEffect(() => {
    if (state?.cryptoAmount && state?.cryptoAmount > 0) {
      handleCryptoAmount(String(state.cryptoAmount));
    } else if (state?.fiatAmount && state?.fiatAmount > 0 && !configLoading) {
      handleFiatAmount(String(state.fiatAmount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  const { rate, isLoading: isRateLoading } = useQuoteRate("sell", state?.cryptoType!, state?.fiatType!, {
    cryptoAmount: Number(state.cryptoAmount ?? 0),
    fiatAmount: Number(state.fiatAmount ?? 0),
  });
  const [_sellCryptoRate, { isLoading: isLoadingRate }] = useGetBuyQuotesMutation();

  const checkSellRate = useCallback(
    async (
      price: number,
      opts?: {
        fiat?: string;
        cryptoAmount?: string;
        coin?: string;
        memo?: string;
        tagNumber?: string;
        network?: string;
        persist?: boolean;
      }
    ) => {
      const { memo, tagNumber, network, cryptoType, fiatAmount, fiatType } = state;

      try {
        const isPreview = previewQuote.current;
        if (!isPreview) {
          isPersistingQuoteRef.current = true;
          setIsPersistingQuote(true);
        }

        const results = await _sellCryptoRate({
          body: {
            cryptoType: String(opts?.coin ? opts?.coin : cryptoType),
            fiatType: String(opts?.fiat ? opts?.fiat : fiatType),
            ...(opts?.cryptoAmount
              ? {
                  cryptoAmount: opts.cryptoAmount,
                }
              : { fiatAmount: String(price ? price : fiatAmount) }),

            network: String(opts?.network ? opts?.network : network || ""),
            memo: String(opts?.memo ? opts?.memo : memo || ""),
            tagNumber: String(opts?.tagNumber ? opts?.tagNumber : tagNumber || ""),
            preview: isPreview,
          },
          token: authToken || "",
          type: "out",
        }).unwrap();

        previewQuote.current = true;

        if (!!results) {
          setRes(results);

          coinToFiatRate.current = +(price ?? 0) / +(results?.quote?.cryptoAmount ?? 1);

          set({ cryptoAmount: results?.quote?.cryptoAmount, fiatAmount: results?.quote?.fiatAmount });
          setQuoteRes(results);
          const quoteId = results.quote.quoteId || "";
          ls.set(configs.QUOTE_ID, quoteId);
          kycRequired.current = results?.quote?.kyc?.kycRequired;
          ls.set(configs.KYC_REQUIRED, kycRequired.current);

          // use for internal state, cos state within redebounce lags behind by 1 frame
          qResRef.current = results;
          // our own home-built async await, when this value is triggered, some wait block is released
          // so it is important that the result is attached to qResRef by setting this value
          lastPersisted.current = !isPreview;

          const offset = 2; // secs offset
          initTime.current = add(new Date(), { seconds: Number(results.quote.duration) + offset });
          sync.current = true;
        } else setRes(null);

        isPersistingQuoteRef.current = false;
        setIsPersistingQuote(false);
      } catch (error) {
        setIsPersistingQuote(false);
        isPersistingQuoteRef.current = false;

        sync.current = false;
      }
    },
    [_sellCryptoRate, authToken, set, setQuoteRes, state]
  );

  const proceedToNext = useCallback(async () => {
    setSellQuote(state);
    const qRes = qResRef.current;
    if (!authToken || qRes?.loginRequired) {
      navigate("/checkout");
      return;
    }

    if ((authToken && kycRequired.current) || qRes?.kyc?.kycRequired) {
      // Fiat Account ID is required, and to select from an existing
      // account, or create a new one, it has to be from the payment page
      navigate("/payment");
      // navigate("/kyc");
      return;
    }

    if (authToken && !kycRequired.current && qRes?.quote?.quoteId) {
      navigate("/payment", { replace: true });
    }
  }, [authToken, navigate, setSellQuote, state]);

  const validateCryptoAmount = useCallback(
    (value: number) => Number(value) >= configData?.minCryptoAmount! && Number(value) <= configData?.maxCryptoAmount!,
    [configData?.maxCryptoAmount, configData?.minCryptoAmount]
  );

  const validateFiatAmount = useCallback(
    (value: number) => {
      return Number(value) >= configData?.minFiatAmount! && Number(value) <= configData?.maxFiatAmount!;
    },
    [configData?.maxFiatAmount, configData?.minFiatAmount]
  );

  const handleFiat = (value: string) => {
    set({ fiatType: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0) return;

    redebounce(() => checkSellRate(0, { fiat: value, cryptoAmount: String(state.cryptoAmount) }), "checkSellRate", 1000)();
  };

  const handleTagNumber = (value: string) => {
    set({ tagNumber: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0) return;
    if (validateFiatAmount(Number(state.fiatAmount)))
      redebounce(() => checkSellRate(+(state?.fiatAmount ?? 0)), "checkSellRate", 1000)();
  };

  const handleMemo = (value: string) => {
    set({ memo: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0) return;
    if (validateFiatAmount(Number(state.fiatAmount)))
      redebounce(() => checkSellRate(+(state?.fiatAmount ?? 0)), "checkSellRate", 1000)();
  };

  const handleCrypto = (coin: string, network?: string) => {
    set({ cryptoType: coin as any, network });
    if (+(state?.fiatAmount ?? 0) <= 0) return;
    if (validateFiatAmount(Number(state.fiatAmount)))
      redebounce(() => checkSellRate(+(state?.fiatAmount ?? 0), { coin, network }), "checkSellRate", 1000)();
  };

  const handleFiatAmount = useCallback(
    (value: string) => {
      const newValue = value;
      set({ fiatAmount: newValue });
      if (+(newValue ?? 0) <= 0) return;

      if (validateFiatAmount(Number(value)))
        redebounce(() => checkSellRate(+(newValue ?? 0), { fiat: state?.fiatType }), "checkSellRate", 1000)();

      if (Number(value) < configData?.minFiatAmount! || Number(value) > configData?.maxFiatAmount!) set({ cryptoAmount: "" });
    },
    [set, validateFiatAmount, redebounce, configData?.minFiatAmount, configData?.maxFiatAmount, checkSellRate, state?.fiatType]
  );

  const refetchQuote = useCallback(
    async (value: string) => {
      set({ fiatAmount: value });
      if (+(value ?? 0) <= 0) return;

      await checkSellRate(+(value ?? 0), {
        fiat: state?.fiatType,
        coin: state?.cryptoType,
        cryptoAmount: String(state?.cryptoAmount),
      });
    },
    [checkSellRate, set, state]
  );

  const handleCryptoAmount = (value: string) => {
    const updatedValue = removeComma(value);
    set({ cryptoAmount: updatedValue });
    // if (+(state?.fiatAmount ?? 0) <= 0) return;
    if (+(updatedValue ?? 0) <= 0) return;

    if (validateCryptoAmount(Number(value)))
      redebounce(() => checkSellRate(+(state?.fiatAmount ?? 0), { cryptoAmount: updatedValue }), "checkSellRate", 1000)();

    if (Number(value) < 5) set({ fiatAmount: "" });
  };

  const persistQuote = useCallback(
    async (e?: any) => {
      if (e) e.preventDefault();

      if (lastPersisted.current && isActiveQuote()) {
        // will not make new request, but will let the function to proceed with existing quote
        return true;
      }

      if ((state.fiatAmount || state.cryptoAmount) && state.cryptoType && state.fiatType) {
        previewQuote.current = false;

        await refetchQuote(String(state.fiatAmount));
      }
      return true;
    },
    [isActiveQuote, refetchQuote, state]
  );

  const formattedCryptoType = useMemo(() => {
    return toCoinLocale(state?.cryptoType! || "");
  }, [state.cryptoType]);

  const isLocked = queries.get("locked");

  const allParameters = useMemo(() => {
    return queries.has("coin") && queries.has("fiat");
  }, [queries]);

  const isDisabled = useMemo(
    () =>
      !(state?.fiatAmount && state?.fiatType && state?.cryptoType && state?.cryptoAmount) ||
      isLoadingRate ||
      // crypto
      (Number(state?.cryptoAmount) < configData?.minCryptoAmount! && !(isLocked === "true" || allParameters)) ||
      (Number(state?.cryptoAmount) > configData?.maxCryptoAmount! && !(isLocked === "true" || allParameters)) ||
      (!!state?.cryptoType && state?.cryptoType === "xrp" && !state?.tagNumber) ||
      (!!state?.cryptoType && state?.cryptoType === "xlm" && !state?.memo) ||
      !quoteRes,
    [
      state?.fiatAmount,
      state?.fiatType,
      state?.cryptoType,
      state?.cryptoAmount,
      state?.tagNumber,
      state?.memo,
      isLoadingRate,
      configData,
      isLocked,
      quoteRes,
      allParameters,
    ]
  );

  const coinPassedInQuery = queries.has("coin");

  const disableCoinAddress = useMemo(() => {
    return (queries.has("coin") && queries.get("locked") === "true") || (queries.has("coin") && queries.has("fiat"));
  }, [queries]);

  const sellingPrice = useMemo(() => {
    const cryptoAmountMinusFees = Number(quoteRes?.quote.cryptoAmount ?? 0) - Number(quoteRes?.quote?.processingFee ?? 0);
    const amount = MathRound(cryptoAmountMinusFees, getCoinPrecision(state?.cryptoType || "", 12));

    return amount;
  }, [quoteRes?.quote.cryptoAmount, quoteRes?.quote?.processingFee, state?.cryptoType]);

  const amountGotten = useMemo(() => {
    const cryptoAmount = quoteRes?.quote?.cryptoAmount ?? 0;

    const fiatAmount =
      Number(quoteRes?.quote.fiatAmount) -
      (quoteRes?.quote?.fiatAmountIncludesWithdrawalFee ? 0 : Number(quoteRes?.quote.fiatWithdrawalFee));

    return `You get ${currencyFormat((state?.fiatType as any) ?? currency).format(
      +(fiatAmount ?? 0)
    )} for ${cryptoAmount.toLocaleString()} ${formattedCryptoType}`;
  }, [quoteRes, state, formattedCryptoType, currency]);

  const actualPrice = useMemo(() => {
    const amt = sellingPrice * Number(rate?.fiatAmount!);

    return amt;
  }, [rate?.fiatAmount, sellingPrice]);

  console.log(actualPrice, "ACTUAL TOTAL AMOUNT");

  const amountTitle = useMemo(() => {
    const amt = `${toPrecision(+(sellingPrice ?? 0), state?.cryptoType ?? configs.DEFAULT_COIN)} @ ${currencyFormat(
      (state?.fiatType as any) ?? currency
    ).format(Number(rate?.fiatAmount) ?? 0)}`;

    return amt;
  }, [currency, rate?.fiatAmount, sellingPrice, state]);

  return (
    <Box>
      <Stack
        as="form"
        my="24px"
        py={{ base: "0", "2sm": "10px" }}
        onSubmit={async (e) => {
          const URL = location.search;
          ls.set(configs.PERSIST_URL, URL);
          await persistQuote(e);

          await proceedToNext();
        }}
      >
        <FormControl mb="20px !important">
          <InputLabel htmlFor="volume" isLoading={isLoadingRate}>
            You send
          </InputLabel>
          <CryptoTxInput
            id="volume"
            placeholder="Equivalent Volume"
            type="number"
            pattern="[\d]*"
            formNoValidate
            value={state.cryptoAmount ?? ""}
            defaultValue={state.cryptoAmount ?? ""}
            coinValue={state?.cryptoType ?? ""}
            network={state?.network ?? ""}
            inputMode="numeric"
            onChange={(e) => handleCryptoAmount(e.target.value)}
            onCoinChange={handleCrypto}
            _select={{ borderColor: "transparent" }}
            disableSelect={coinPassedInQuery || disableCoinAddress}
            walletType={tokens}
            selectTitle="Which crypto do you want to buy?"
            isNetworkRequired
          >
            {(tokens ?? [])?.map((token) => (
              <option key={`buy-crypto-${token}`} value={token}>
                {toCoinLocale(token)}
              </option>
            ))}
          </CryptoTxInput>

          {state.cryptoAmount && configData ? (
            <>
              {state.cryptoAmount && Number(state.cryptoAmount) < Number(configData?.minCryptoAmount) && (
                <Text mt="4px" color="accent.500" fontWeight="500" fontSize="14px">
                  Amount is too low. Minimum is {addCommas(configData?.minCryptoAmount)} {String(state.cryptoType).toUpperCase()}
                </Text>
              )}

              {state.cryptoAmount && Number(state.cryptoAmount) > Number(configData?.maxCryptoAmount) && (
                <Text mt="4px" color="accent.500" fontWeight="500" fontSize="14px">
                  Amount is too high. Maximum is {configData?.maxCryptoAmount.toLocaleString()}{" "}
                  {String(state.cryptoType).toUpperCase()}
                </Text>
              )}
            </>
          ) : (
            <Text mt="4px" color="black.400" fontWeight="500" fontSize="14px">
              Minimum crypto amount is {addCommas(configData?.minCryptoAmount!)} {toCoinLocale(state.cryptoType!)}
            </Text>
          )}
        </FormControl>

        <HStack>
          {!disableCoinAddress && (
            <>
              {!!state?.cryptoType && state?.cryptoType === "xlm" && (
                <FormControl flex="25%" mb="20px !important">
                  <InputLabel>Memo</InputLabel>
                  <Input
                    isRequired
                    type="text"
                    disabled={queries.has("memo")}
                    value={state?.memo ?? ""}
                    onChange={(e) => handleMemo(e.target.value)}
                  />
                </FormControl>
              )}

              {!!state?.cryptoType && state?.cryptoType === "xrp" && (
                <FormControl flex="25%" mb="20px !important">
                  <InputLabel>Tag Number</InputLabel>
                  <Input
                    isRequired
                    type="text"
                    disabled={queries.has("tagNumber")}
                    value={state?.tagNumber ?? ""}
                    onChange={(e) => handleTagNumber(e.target.value)}
                  />
                </FormControl>
              )}
            </>
          )}
        </HStack>

        {!disableCoinAddress && (
          <FormControl mb="20px !important">
            <Flex justifyContent="space-between">
              <InputLabel htmlFor="amount">You receive {toUpperTrim(state.fiatType || "Fiat")}</InputLabel>

              <InputLabel fontSize="11px" color="green.500" _text={{ fontWeight: "700" }}>
                {configLoading && <CircularLoader />}

                {qRes && Number(qRes?.quote.fiatWithdrawalFee) > 0 && (
                  <>
                    +{qRes.quote?.fiatWithdrawalFee}{" "}
                    <Text as="span" textTransform="uppercase">
                      {state.fiatType}{" "}
                    </Text>
                    withdrawal fee
                  </>
                )}
              </InputLabel>
            </Flex>
            <CryptoTxInput
              id="amount"
              placeholder="Enter Amount"
              value={state.fiatAmount ?? ""}
              defaultValue={state.fiatAmount ?? ""}
              type="number"
              coinValue={state?.fiatType ?? ""}
              onChange={(e) => handleFiatAmount(e.target.value)}
              pattern="[\d]*"
              formNoValidate
              inputMode="numeric"
              onCoinChange={handleFiat}
              _select={{ borderColor: "transparent" }}
              walletType={configs.fiats}
              selectTitle="Which currency do you want to pay with?"
              disableSelect={disableCoinAddress && queries.has("fiat")}
            >
              {(configs.fiats ?? [])?.map((fiat) => (
                <option key={`buy-crypto-${fiat}`} value={fiat}>
                  {toCoinLocale(fiat)}
                </option>
              ))}
            </CryptoTxInput>
          </FormControl>
        )}

        <SwapInfo isLoading={isRateLoading}>
          1{" "}
          <Text as="span" textTransform="initial">
            {formattedCryptoType}
          </Text>{" "}
          = {rate?.fiatAmount ? currencyFormat((state?.fiatType as any) ?? currency).format(Number(rate?.fiatAmount) ?? 0) : ""}
        </SwapInfo>

        {!!quoteRes && validateCryptoAmount(Number(state.cryptoAmount)) && (
          <>
            <ExchangeInfoAccordion title={amountGotten} mt="24px" isOpen={false}>
              <ExchangeInfoDetail
                title={amountTitle}
                description={currencyFormat((state?.fiatType as any) ?? currency).format(+(actualPrice ?? 0))}
              />

              <ExchangeInfoDetail
                title="Total Fiat Fee"
                description={currencyFormat((state?.fiatType as any) ?? currency).format(+(quoteRes.quote.totalFiatFee ?? 0))}
              />

              <ExchangeInfoDetail
                title="Total Crypto Fee"
                description={`${quoteRes?.quote?.totalCryptoFee || 0} `.concat(formattedCryptoType)}
              />
            </ExchangeInfoAccordion>
          </>
        )}

        <VStack mt="34px !important">
          <Button
            w="100%"
            minW={{ base: "auto", "2sm": "400px" }}
            type="submit"
            isDisabled={isDisabled || isPersistingQuote}
            isLoading={isPersistingQuote || isPersistingQuoteRef.current}
            _hover={{
              _disabled: {
                opacity: 0.3,
              },
            }}
          >
            Continue
          </Button>
        </VStack>
      </Stack>
    </Box>
  );
}
